import { useEffect } from "react";

import { track } from "../analytics/publicAnalytics";
import { Link } from "../components/Link";
import { Logo } from "../components/Logo";
import { ManualMenu } from "../components/ManualMenu";
import { MANUAL_HELP, MANUAL_STEPS, sceneSrc } from "../manual/content";
import { SITE_CONTACT } from "../seo/site";

/**
 * 그림으로 보는 이용 매뉴얼. 로그인 없이 누구나 본다. 로그인한 사람에게는
 * 셸 헤더가 이미 있으므로 자체 헤더를 그리지 않는다.
 */
export const ManualPage = ({ signedIn = false }: { signedIn?: boolean }) => {
  // A link such as /manual#step-3 lands before the page renders (the shell
  // first checks the session), so the browser's own jump finds nothing.
  useEffect(() => {
    try {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (id) {
        document.getElementById(id)?.scrollIntoView?.();
      }
    } catch {
      // A malformed fragment is ignored.
    }
  }, []);

  return (
    <div className="canvaschool-landing canvaschool-manual">
      {!signedIn && (
        <header className="canvaschool-landing-header canvaschool-landing-bar cm-bar">
          <Link to="/" className="canvaschool-brand">
            <Logo size={28} />
            크루바스
          </Link>
          <nav className="cl-nav" aria-label="바로가기">
            <ManualMenu variant="landing" />
            <Link to="/login" className="cl-nav-login">
              로그인
            </Link>
            <Link
              to="/login"
              className="cl-btn cl-btn--accent cl-nav-cta"
              onClick={() =>
                track("beta_cta_click", { cta_position: "manual" })
              }
            >
              무료로 시작하기
            </Link>
          </nav>
        </header>
      )}

      <section className="cm-hero" aria-labelledby="cm-title">
        <div className="cl-grid">
          <p className="cm-kicker">이용 방법</p>
          <h1 id="cm-title" className="cl-name cm-title">
            세 단계면
            <br />첫 수업이 열립니다
          </h1>
          <p className="cm-lead">
            학급을 만들고, 캔버스를 만들고, 학생이 들어와 그립니다. 실제
            크루바스(이전 이름 크루버스) 화면을 순서대로 따라가 보세요.
          </p>
          <ol className="cm-overview">
            {MANUAL_STEPS.map((step) => (
              <li key={step.id}>
                <a href={`#${step.id}`} className="cm-overview-card">
                  <span className="cm-overview-number" aria-hidden="true">
                    {step.number}
                  </span>
                  <span className="cm-overview-title">{step.title}</span>
                  <span className="cm-overview-meta">
                    {step.who} · {step.time}
                  </span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {MANUAL_STEPS.map((step) => (
        <section
          key={step.id}
          id={step.id}
          className={step.number === 2 ? "cm-step cm-step--tint" : "cm-step"}
          aria-labelledby={`${step.id}-title`}
        >
          <div className="cl-grid cm-step-head">
            <p className="cm-step-number" aria-hidden="true">
              {step.number}
            </p>
            <div className="cm-step-heading">
              <p className="cm-step-meta">
                {step.number}단계 · {step.who} · {step.time}
              </p>
              <h2 id={`${step.id}-title`} className="cl-name">
                {step.title}
              </h2>
              <p className="cm-step-lead">{step.lead}</p>
            </div>
          </div>
          <ol className="cm-scenes">
            {step.scenes.map((scene, index) => (
              <li key={scene.id} id={scene.id} className="cl-grid cm-scene">
                <div className="cm-scene-copy">
                  <p className="cm-scene-label">
                    장면 {step.number}-{index + 1}
                  </p>
                  <h3 className="cm-scene-title">{scene.title}</h3>
                  <p className="cm-scene-body">{scene.body}</p>
                </div>
                {/* 휴대폰에서는 그림이 작아서, 누르면 원본 크기로 연다. */}
                <a
                  className="cm-shot"
                  href={sceneSrc(scene.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={sceneSrc(scene.id)}
                    alt={scene.alt}
                    width={1280}
                    height={800}
                    loading={
                      step.number === 1 && index === 0 ? "eager" : "lazy"
                    }
                    decoding="async"
                  />
                </a>
              </li>
            ))}
          </ol>
          {step.number === 1 && (
            <p className="cl-grid cm-step-aside">
              <span>
                학급을 만들기 전에 보고 싶다면 칸이 비어 있는 공개 안내문도
                있어요. <Link to="/manual/student">빈칸 안내문 인쇄하기</Link>
              </span>
            </p>
          )}
        </section>
      ))}

      <section className="cl-faq cm-help" aria-labelledby="cm-help-title">
        <div className="cl-grid cl-faq-grid">
          <h2 id="cm-help-title" className="cl-q cl-muted">
            자주 막히는 곳
          </h2>
          {MANUAL_HELP.map((item) => (
            <div key={item.q} className="cl-faq-row">
              <h3 className="cl-q">{item.q}</h3>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="cm-handout" aria-labelledby="cm-handout-title">
        <div className="cl-grid cm-handout-grid">
          <h2 id="cm-handout-title" className="cl-name">
            학생에게는
            <br />한 장이면 됩니다
          </h2>
          <div className="cm-handout-text">
            <p>
              로그인부터 내 칸에 그리기까지 네 장면을 A4 한 장에 담았습니다.
              학급 화면의 「학생별 안내문 인쇄」를 쓰면 아이디와 처음 비밀번호가
              채워져 아이마다 한 장씩 나옵니다. 아래 공개 안내문은 두 칸이
              빈칸이라 직접 적어 나눠 줍니다.
            </p>
            <Link to="/manual/student" className="cl-btn cl-btn--paper">
              빈칸 안내문 열기
            </Link>
          </div>
        </div>
      </section>

      <footer className="cm-footer">
        <div className="cl-grid">
          <p>
            더 궁금한 점은 <a href={`mailto:${SITE_CONTACT}`}>{SITE_CONTACT}</a>
            로 보내 주세요.
          </p>
        </div>
      </footer>
    </div>
  );
};
